import React, { useState, useEffect } from 'react';
import { projectsApi, Project } from '../../../api/projectsApi';
import { projectRequestsApi } from '../../../api/projectRequestsApi';
import { Icons } from '../../../components/common/Icons';
import { Button } from '../../../components/common/Button';
import { Loader } from '../../../components/common/Loader';
import './ProjectRequestModal.css';

const MAX_REASON_LENGTH = 120;

interface ProjectRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function ProjectRequestModal({ isOpen, onClose, onSuccess }: ProjectRequestModalProps) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchUnassignedProjects();
    }
  }, [isOpen]);

  const fetchUnassignedProjects = async () => {
    try {
      setLoadingProjects(true);
      setError(null);
      const response = await projectsApi.getUnassignedProjects();
      setProjects(response.projects);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedProjectId) {
      setError('Please select a project');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await projectRequestsApi.createRequest({
        project_id: selectedProjectId,
        reason: reason.trim() || undefined,
      });
      
      // Reset form
      setSelectedProjectId(null);
      setReason('');
      
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedProjectId(null);
      setReason('');
      setProjectSearch('');
      setError(null);
      onClose();
    }
  };

  const filteredProjects = projects.filter(p =>
    p.name.toLowerCase().includes(projectSearch.toLowerCase()) ||
    (p.client_name || '').toLowerCase().includes(projectSearch.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Request Project Assignment</h2>
          <button className="modal-close-btn" onClick={handleClose} disabled={loading}>
            <Icons.X />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {error && (
            <div className="modal-error">
              <Icons.AlertCircle />
              <span>{error}</span>
            </div>
          )}

          {loadingProjects ? (
            <div className="modal-loading">
              <Loader text="Loading projects..." />
            </div>
          ) : projects.length === 0 ? (
            <div className="modal-empty">
              <Icons.Folder />
              <p>No available projects to request</p>
              <span>You are already assigned to all active projects or no projects exist.</span>
            </div>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="project">Select Project <span className="required">*</span></label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Search projects by name or client..."
                  value={projectSearch}
                  onChange={(e) => setProjectSearch(e.target.value)}
                  disabled={loading}
                />
                <div className="project-list">
                  {filteredProjects.length === 0 ? (
                    <p className="no-results">No projects found matching "{projectSearch}"</p>
                  ) : (
                    filteredProjects.map((project) => (
                      <div
                        key={project.id}
                        className={`project-option ${selectedProjectId === project.id ? 'selected' : ''}`}
                        onClick={() => setSelectedProjectId(project.id)}
                      >
                        <div className="project-option-info">
                          <span className="project-option-name">{project.name}</span>
                          {project.client_name && (
                            <span className="project-option-meta">{project.client_name}</span>
                          )}
                        </div>
                        {selectedProjectId === project.id && <Icons.Check />}
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="reason">Reason for Request (Optional)</label>
                <textarea
                    id="reason"
                    value={reason}
                    onChange={(e) => {
                      if (e.target.value.length <= MAX_REASON_LENGTH) {
                        setReason(e.target.value);
                      }
                    }}
                    maxLength={MAX_REASON_LENGTH}
                    placeholder="Enter the reason for requesting this project..."
                    rows={4}
                    disabled={loading}
                    className="form-textarea"
                  />
                  <p
                      style={{
                        fontSize: '12px',
                        marginTop: '4px',
                        color: reason.length > 450 ? 'red' : '#888',
                      }}
                    >
                      {reason.length}/{MAX_REASON_LENGTH}
                    </p>
              </div>
            </>
          )}

          <div className="modal-footer">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            {projects.length > 0 && (
              <Button
                type="submit"
                variant="primary"
                disabled={loading || !selectedProjectId}
              >
                {loading ? 'Submitting...' : 'Submit Request'}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProjectRequestModal;
